import React from "react";
import { useMemo } from "react";
import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GainLossPercents } from "@components";
import { toCurrency } from "@helpers";
import { computeDividendKpis } from "./helpers";
import type { DividendPerYear } from "@bn/api/apiv1";

interface Props {
  dividends?: DividendPerYear[];
  currency?: string;
}

const Container = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(4),
  padding: theme.spacing(0, 4, 1),
}));

const Cell = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  minWidth: 0,
}));

const ValueRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

export const DividendsKpiStrip = ({ dividends, currency = "EUR" }: Props) => {
  const { t } = useTranslation();

  const kpis = useMemo(() => computeDividendKpis(dividends), [dividends]);
  if (!kpis) return null;

  return (
    <Container data-testid="dividends-kpi-strip">
      <Cell>
        <Typography variant="caption" color="textSecondary">
          {t("fundamentals.dividends.kpi.latestDividend", {
            year: kpis.latestYear,
          })}
        </Typography>
        <ValueRow>
          <Typography variant="body2" color="textPrimary">
            {toCurrency(kpis.latestValue, currency)}
          </Typography>
          {kpis.latestYearlyChange !== null && (
            <GainLossPercents value={kpis.latestYearlyChange} size="small" />
          )}
        </ValueRow>
      </Cell>
      <Cell>
        <Typography variant="caption" color="textSecondary">
          {t("fundamentals.dividends.kpi.averageAndSum", {
            count: kpis.count,
          })}
        </Typography>
        <ValueRow>
          <Typography variant="body2" color="textPrimary">
            {toCurrency(kpis.average, currency)}
          </Typography>
          <Typography variant="body3" color="textSecondary">
            · {toCurrency(kpis.sum, currency)}
          </Typography>
        </ValueRow>
      </Cell>
    </Container>
  );
};
